// deps
import { useContext, useEffect, useRef, useState, useCallback } from "react";

// Contexts
import { CartOverlaysContext } from "@checkout-core/contexts/CartOverlaysContext";

// Types
import { GlobalOverlayPriority, LottieIds, OverlayType } from "@swiggy-private/cart-types";

export interface UseSavingsBannerAnimationParams {
    shouldRegister: boolean;
}

export interface UseSavingsBannerAnimationReturn {
    shouldPlayAnimation: boolean;
    onAnimationComplete: () => void;
}

export const useSavingsBannerAnimation = ({
    shouldRegister,
}: UseSavingsBannerAnimationParams): UseSavingsBannerAnimationReturn => {
    const { registerOverlays, enabledOverlayId, onCloseOverlay } = useContext(CartOverlaysContext);

    const isRegisteredRef = useRef(false);
    const [hasAnimationStarted, setHasAnimationStarted] = useState(false);

    useEffect(() => {
        if (!shouldRegister || isRegisteredRef.current) {
            return;
        }

        registerOverlays({
            overlayType: OverlayType.SAVINGS_ANIMATION,
            priority: GlobalOverlayPriority.SAVINGS_BANNER_ANIMATION,
            id: LottieIds.SAVINGS_BANNER_ANIMATION,
        });

        isRegisteredRef.current = true;
    }, [shouldRegister, registerOverlays]);

    useEffect(() => {
        if (enabledOverlayId === LottieIds.SAVINGS_BANNER_ANIMATION && !hasAnimationStarted) {
            setHasAnimationStarted(true);
        }
    }, [enabledOverlayId, hasAnimationStarted]);

    const onAnimationComplete = useCallback(() => {
        if (enabledOverlayId === LottieIds.SAVINGS_BANNER_ANIMATION) {
            onCloseOverlay();
        }
    }, [enabledOverlayId, onCloseOverlay]);

    return {
        shouldPlayAnimation: hasAnimationStarted,
        onAnimationComplete,
    };
};
